import { J3Ok } from '@e3dc-react/icons'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DEFAULT_HUMAN_TIME, format } from '@e3dc-react/shell/Libs/date'
import { Stack, Theme, useMediaQuery, useTheme, Box, Button, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useUserRoles } from 'src/Hooks/user'
import { SystemlogModel } from 'src/Libs/installations/models'
import { SystemlogType } from 'src/Libs/installations/types'
import { makeStyles } from 'tss-react/mui'

/**
 * Returns the borderColor of the protocol accordion depending on error log item type
 */
const getBorderColor = (type: SystemlogType, theme: Theme, isResolved: boolean): string => {
  if (isResolved) {
    return theme.palette.primary.main
  }
  if (type === SystemlogType.Error) {
    return theme.palette.error.main
  }
  if (type === SystemlogType.Warning) {
    return theme.palette.warning.main
  }
  return theme.palette.primary.main
}

/**
 * Returns the borderColor of the protocol accordion depending on error log item type
 */
const getTextColor = (type: SystemlogType, theme: Theme): string => {
  if (type === SystemlogType.Error) return theme.palette.error.main
  if (type === SystemlogType.Warning) return theme.palette.warning.main
  return theme.palette.text.primary
}

interface StyleProps {
  type: SystemlogType
  isResolved: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  const containerBorderColor = getBorderColor(props.type, theme, props.isResolved)
  const accordionBorderColor = props.isResolved ? theme.palette.shades.shade03 : getBorderColor(props.type, theme, props.isResolved)
  const typeColor = props.isResolved ? theme.palette.text.primary : getTextColor(props.type, theme)

  return {
    container: {
      position: 'relative',
      paddingLeft: theme.spacing(2),
      backgroundColor: 'inherit',
      width: '100%',
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 17,
        height: 17,
        right: 17,
        left: -13,
        backgroundColor: theme.palette.background.paper,
        border: `3px solid ${containerBorderColor}`,
        top: '38%',
        borderRadius: '50%',
        zIndex: 1,
      },
      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
      },
      marginBottom: theme.spacing(1),
    },
    accordion: {
      borderRadius: `${theme.shape.borderRadius}px!important`,
      backgroundColor: theme.palette.shades.shade04,
      borderColor: accordionBorderColor,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(1)} 0px`,
      },
      '.MuiAccordionSummary-expandIconWrapper': {
        color: theme.palette.text.primary,
      },
      '.MuiAccordionSummary-content': {
        cursor: 'pointer',
      },
    },
    accordionTitle: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      flexDirection: 'row',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
      },
    },
    type: {
      color: typeColor,
      minWidth: '10%',
    },
    description: {
      marginLeft: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        maxWidth: '100%',
        paddingBottom: theme.spacing(1),
      },
      color: theme.palette.text.primary,
    },
    headerDate: {
      color: theme.palette.text.primary,
    },
    headerTime: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(1),
      color: theme.palette.shades.shade01,
    },
    content: {
      color: theme.palette.shades.shade01,
    },
    footerContent: {
      marginTop: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
    },
    footer: {
      borderTop: `1px solid ${theme.palette.shades.shade03}`,
      marginTop: theme.spacing(2),
    },
    icon: {
      height: 24,
      width: 24,
      color: theme.palette.primary.dark,
    },
    resolveButton: {
      display: 'flex',
      justifyContent: 'center',
    },
    headerChildrenWrapper: {
      marginRight: theme.spacing(2),
    },
    headerContent: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      maxWidth: '100%',
      '&.Mui-expanded': {
        minHeight: theme.spacing(8),
      },
    },
    accordionContent: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    resolvedAt: {
      color: theme.palette.text.primary,
    },
  }
})

interface SystemProtocolAccordionProps {
  log: SystemlogModel
  installationId?: string
  serialNumber?: string
  displaySerialNumber: boolean
  onResolveSystemLog: (installationId: number, logId: number) => void
}

/**
 * Displays information about a system log entry within an accordion and allows certain user groups to mark logs as resolved
 */
const SystemProtocolAccordion: React.FC<SystemProtocolAccordionProps> = ({
  log,
  installationId,
  serialNumber,
  displaySerialNumber,
  onResolveSystemLog,
}) => {
  const { classes } = useStyles({ type: log.errorType, isResolved: log.isResolved })
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const createdAtDate = new Date(log.createdAt)
  const resolvedAtDate = new Date(log.resolvedAt)

  const { t } = useCustomTranslation()
  const { isInstaller, isServiceUser, isPartner } = useUserRoles()
  const canResolveLog = useMemo(() => isInstaller || isServiceUser || isPartner, [isInstaller, isServiceUser, isPartner])

  const handleResolveSystemLog = (): void => {
    if (!canResolveLog) return
    onResolveSystemLog(Number(installationId), log.id)
  }

  return (
    <Box key={log.id} className={classes.container}>
      <AccordionItem
        contentClassName={classes.accordionContent}
        title={
          <Box className={classes.accordionTitle}>
            {downSm ? (
              <Stack sx={{ width: '100%' }} direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" className={classes.type}>
                  {t(`systemlog.type.${log.errorType.toLowerCase()}`)}
                </Typography>
                <Stack direction="row" justifyContent={'flex-end'} alignItems="center">
                  {log.isResolved && <J3Ok className={classes.icon} />}
                  <Typography variant="subtitle2" className={classes.headerDate}>
                    {format(new Date(log.createdAt), t('common.format.date-fns-date'))}
                  </Typography>
                  <Typography variant="caption" className={classes.headerTime}>
                    {format(createdAtDate, DEFAULT_HUMAN_TIME)}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography variant="subtitle2" className={classes.type}>
                {t(`systemlog.type.${log.errorType.toLowerCase()}`)}
              </Typography>
            )}
            <Typography variant="subtitle2" className={classes.description}>
              {displaySerialNumber && serialNumber ? serialNumber + ': ' : ''}
              {t(`errors.notification.email.${log.errorType}-${log.errorSource}-${log.errorCode}.subject`)}
            </Typography>
          </Box>
        }
        className={classes.accordion}
        headerChildren={
          !downSm && (
            <Stack direction="row" alignItems="center" spacing={2} className={classes.headerChildrenWrapper}>
              {log.isResolved && <J3Ok className={classes.icon} />}
              <Typography variant="subtitle2" className={classes.headerDate}>
                {format(new Date(log.createdAt), t('common.format.date-fns-date'))}
              </Typography>
              <Typography variant="caption" className={classes.headerTime}>
                {format(createdAtDate, DEFAULT_HUMAN_TIME)}
              </Typography>
            </Stack>
          )
        }
        headerContentClassName={classes.headerContent}
      >
        <Stack direction="column" spacing={2}>
          <Typography className={classes.content} variant="caption">
            {t(`errors.notification.email.${log.errorType}-${log.errorSource}-${log.errorCode}.message`)}
          </Typography>
          {log.isResolved ? (
            <Box className={classes.footer}>
              <Box className={classes.footerContent}>
                {log.resolvedAt && (
                  <Typography variant="subtitle2">
                    <span className={classes.resolvedAt}>{t('common.resolvedAt')}: </span>
                    {format(new Date(log.resolvedAt), t('common.format.date-fns-date'))}
                  </Typography>
                )}
                {!isNaN(resolvedAtDate.getDate()) && (
                  <Typography variant="subtitle2" className={classes.headerTime}>
                    {format(resolvedAtDate, DEFAULT_HUMAN_TIME)}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            canResolveLog && (
              <Box display="flex" justifyContent="center">
                <Button className={classes.resolveButton} onClick={handleResolveSystemLog}>
                  <Typography>{t('common.resolved')}</Typography>
                </Button>
              </Box>
            )
          )}
        </Stack>
      </AccordionItem>
    </Box>
  )
}

export default SystemProtocolAccordion
